import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Box,
  Typography,
  TableRow,
  TableCell,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import fetchWithAuth from "../../../../util/fetchWithAuth";
import { API_URL } from "../../../../util/url";
import StyledTable from "../../../../shared/StyledTable/StyledTable";
import { useNavigate } from "react-router-dom";
// ==== Стилизация аккордеона ====
const Accordion = styled((props) => (
  <MuiAccordion
    className="rmui-card"
    disableGutters
    elevation={0}
    square
    {...props}
  />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      <ArrowForwardIosSharpIcon
        sx={{ fontSize: "0.9rem", color: "text.secondary" }}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row",
  "& .MuiAccordionSummary-content": {
    marginRight: theme.spacing(1),
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    marginLeft: "auto",
  },
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: 0,
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const LAST_PANEL_KEY = "scriptsLastPanelId";
const LAST_PRODUCT_KEY = "scriptsLastProductId";
const LAST_TAB_KEY = "scriptsTabIndex";
const FILTERS_KEY = "scriptsCrmFilters";

const getDisplayProductId = (product, isCross) =>
  product?.product_id ??
  product?.crm_product_id ??
  product?.external_id ??
  (isCross ? product?.cross_id : product?.id);

const getProductQuantity = (product, isCross) =>
  Number(isCross ? product?.quantity : product?.sold_today ?? 0) || 0;

const getProductPrice = (product) =>
  Number(String(product?.price || "").replace(",", ".")) || 0;

const filterProducts = (items, filters, isCross) => {
  const safeItems = Array.isArray(items) ? items : [];
  const query = (filters.query || "").trim().toLowerCase();
  const mode = filters.mode || "all";
  const onlyPopular = Boolean(filters.onlyPopular);
  const sort = filters.sort || "none";

  let next = safeItems.filter((item) => {
    if (onlyPopular && getProductQuantity(item, isCross) <= 0) return false;
    if (!query) return true;
    const title = String(item?.title || "").toLowerCase();
    const idValue = String(getDisplayProductId(item, isCross) ?? "").toLowerCase();
    if (mode === "title") return title.includes(query);
    if (mode === "id") return idValue.includes(query);
    return title.includes(query) || idValue.includes(query);
  });

  if (sort === "quantity_desc") {
    next = [...next].sort(
      (a, b) => getProductQuantity(b, isCross) - getProductQuantity(a, isCross)
    );
  } else if (sort === "quantity_asc") {
    next = [...next].sort(
      (a, b) => getProductQuantity(a, isCross) - getProductQuantity(b, isCross)
    );
  } else if (sort === "price_desc") {
    next = [...next].sort((a, b) => getProductPrice(b) - getProductPrice(a));
  } else if (sort === "price_asc") {
    next = [...next].sort((a, b) => getProductPrice(a) - getProductPrice(b));
  } else if (sort === "id_desc") {
    next = [...next].sort((a, b) =>
      String(getDisplayProductId(b, isCross) ?? "").localeCompare(
        String(getDisplayProductId(a, isCross) ?? ""),
        undefined,
        { numeric: true }
      )
    );
  } else if (sort === "id_asc") {
    next = [...next].sort((a, b) =>
      String(getDisplayProductId(a, isCross) ?? "").localeCompare(
        String(getDisplayProductId(b, isCross) ?? ""),
        undefined,
        { numeric: true }
      )
    );
  }

  return next;
};

// ==== Функция для строки товара ====
const renderProductRow = (product, onClick, onSelect, isCross) => (
  <TableRow
    key={isCross ? product.cross_id : product.id}
    onClick={() => {
      const productId = isCross ? product.cross_id : product.id;
      onSelect?.(productId);
      onClick(productId);
    }}
    sx={{ cursor: "pointer" }}
    data-product-id={isCross ? product.cross_id : product.id}
  >
    <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          component="img"
          src={`${product.image}`}
          alt={product.title}
          sx={{
            width: 46,
            height: 46,
            objectFit: "cover",
            borderRadius: 1,
          }}
        />
        <Typography variant="subtitle2" fontWeight={500}>
          {product.title}
        </Typography>
      </Box>
    </TableCell>
    <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
      {getDisplayProductId(product, isCross)}
    </TableCell>
    <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
      {isCross ? product.quantity : product.sold_today ?? 0}
    </TableCell>
    <TableCell className="border-bottom" sx={{ padding: "12px 20px" }}>
      <Typography fontWeight={600}>
        {product.price && product.price !== "0.00" ? `${product.price} ₴` : "—"}
      </Typography>
    </TableCell>
  </TableRow>
);

// ==== Основной компонент ====
const TabContent = ({ tabIndex }) => {
  const isCrossTab = tabIndex !== 0;
  const [data, setData] = useState([]);
  const [expanded, setExpanded] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(LAST_PANEL_KEY);
  });
  const hasRestoredPanel = useRef(false);
  const [loading, setLoading] = useState(true);
  const [filtersByGroup, setFiltersByGroup] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.sessionStorage.getItem(FILTERS_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed?.[String(tabIndex ?? 0)] || {};
    } catch (err) {
      console.warn("Failed to read CRM filters", err);
      return {};
    }
  });
  const navigate = useNavigate();
  const handleChange = (panel) => (_, newExpanded) => {
    const next = newExpanded ? panel : false;
    setExpanded(next);
    if (typeof window === "undefined") return;
    if (newExpanded) {
      window.sessionStorage.setItem(LAST_PANEL_KEY, panel);
    } else {
      window.sessionStorage.removeItem(LAST_PANEL_KEY);
    }
  };

  const handleClick = (id) => {
    if (!isCrossTab) {
      navigate(`/supervisor/scripts/${id}`);
    }
    if (isCrossTab) {
      navigate(`/supervisor/cross-product/${id}`);
    }
  };

  useEffect(() => {
    const fetchCrmData = async () => {
      try {
        const res = await fetchWithAuth(API_URL + "/statistic/crm-products");
        if (!res.ok) throw new Error("Failed to load CRM products");
        const json = await res.json();
        setData(json);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCrmData();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem(FILTERS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const tabKey = String(tabIndex ?? 0);
      setFiltersByGroup(parsed?.[tabKey] || {});
    } catch (err) {
      console.warn("Failed to restore CRM filters", err);
    }
  }, [tabIndex]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem(FILTERS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const tabKey = String(tabIndex ?? 0);
      parsed[tabKey] = filtersByGroup;
      window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify(parsed));
    } catch (err) {
      console.warn("Failed to save CRM filters", err);
    }
  }, [filtersByGroup, tabIndex]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!Array.isArray(data) || data.length === 0) return;
    if (hasRestoredPanel.current) return;
    const savedPanel = window.sessionStorage.getItem(LAST_PANEL_KEY);
    if (savedPanel) {
      setExpanded(savedPanel);
    }
    hasRestoredPanel.current = true;
  }, [data]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!Array.isArray(data) || data.length === 0) return;
    const savedProduct = window.sessionStorage.getItem(LAST_PRODUCT_KEY);
    if (!savedProduct) return;
    const target = `[data-product-id="${savedProduct}"]`;
    const scrollToTarget = () => {
      const row = document.querySelector(target);
      if (row) {
        row.scrollIntoView({ block: "center" });
      }
    };
    const rafId = requestAnimationFrame(() => {
      scrollToTarget();
      setTimeout(scrollToTarget, 0);
    });
    return () => cancelAnimationFrame(rafId);
  }, [data, expanded]);

  const updateFilters = (groupIndex, patch) => {
    setFiltersByGroup((prev) => {
      const current = prev[groupIndex] || {};
      return { ...prev, [groupIndex]: { ...current, ...patch } };
    });
  };

  const handleSelectProduct = (panelId, productId) => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(LAST_PANEL_KEY, panelId);
    window.sessionStorage.setItem(LAST_PRODUCT_KEY, String(productId));
    window.sessionStorage.setItem(LAST_TAB_KEY, String(tabIndex ?? 0));
  };

  const getFilters = (groupIndex) => ({
    query: "",
    mode: "all",
    onlyPopular: false,
    sort: "none",
    ...(filtersByGroup[groupIndex] || {}),
  });

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  return (
    <Card className="rmui-card">
      {data.map((group, groupIndex) => {
        const panelId = `panel${groupIndex + 1}`;
        const items = isCrossTab ? group.corss_products : group.products;
        const filters = getFilters(groupIndex);
        const filteredItems = filterProducts(items, filters, isCrossTab);
        return (
          <Accordion
            key={panelId}
            expanded={expanded === panelId}
            onChange={handleChange(panelId)}
            sx={{ mb: 1 }}
            className="rmui-card"
          >
            <AccordionSummary
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}
              sx={{ padding: "10px 25px" }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                fontSize="15px"
                flexGrow={1}
              >
                {group.name}
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Box sx={{ px: 2, py: 1.5, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Пошук…"
                  value={filters.query}
                  onChange={(event) =>
                    updateFilters(groupIndex, { query: event.target.value })
                  }
                  sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id={`filter-mode-${groupIndex}`}>Пошук по</InputLabel>
                  <Select
                    labelId={`filter-mode-${groupIndex}`}
                    label="Пошук по"
                    value={filters.mode}
                    onChange={(event) =>
                      updateFilters(groupIndex, { mode: event.target.value })
                    }
                  >
                    <MenuItem value="all">Назва або ID</MenuItem>
                    <MenuItem value="title">Назва</MenuItem>
                    <MenuItem value="id">ID</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id={`filter-sort-${groupIndex}`}>Сортування</InputLabel>
                  <Select
                    labelId={`filter-sort-${groupIndex}`}
                    label="Сортування"
                    value={filters.sort}
                    onChange={(event) =>
                      updateFilters(groupIndex, { sort: event.target.value })
                    }
                  >
                    <MenuItem value="none">Без сортування</MenuItem>
                    <MenuItem value="quantity_desc">Кількість ↓</MenuItem>
                    <MenuItem value="quantity_asc">Кількість ↑</MenuItem>
                    <MenuItem value="price_desc">Ціна ↓</MenuItem>
                    <MenuItem value="price_asc">Ціна ↑</MenuItem>
                    <MenuItem value="id_desc">ID ↓</MenuItem>
                    <MenuItem value="id_asc">ID ↑</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.onlyPopular}
                      onChange={(event) =>
                        updateFilters(groupIndex, {
                          onlyPopular: event.target.checked,
                        })
                      }
                    />
                  }
                  label="Тільки з кількістю > 0"
                />
                <Typography variant="body2" color="text.secondary">
                  Показано: {filteredItems.length} з {Array.isArray(items) ? items.length : 0}
                </Typography>
              </Box>
              <StyledTable
                headers={["Назва товару", "ID", "Кількість", "Ціна"]}
                rows={filteredItems}
                renderRow={(row) =>
                  renderProductRow(
                    row,
                    handleClick,
                    (productId) => handleSelectProduct(panelId, productId),
                    isCrossTab
                  )
                }
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Card>
  );
};

export default TabContent;
